// 🚀 Serviços de integração com Supabase
// Baseado na estrutura real do banco de dados

// Tipos locais para evitar problemas de importação
interface Cliente {
  id: string | number
  nome: string
  email: string
  whatsapp?: string
  cpf?: string
  auth_user_id?: string
  created_at?: string
  updated_at?: string
}

interface Pedido {
  id: string | number
  id_cliente: string | number
  data_criacao: string
  total: number
  metodo_pagamento?: string
  status_pagamento: string
  origem_pedido?: string
  webhook?: string
  created_at?: string
  updated_at?: string
}

interface Livro {
  id: string | number
  id_livro?: string | number
  id_pedido: string
  titulo: string
  subtitulo?: string
  assunto?: string
  idioma?: string
  nome_autor?: string
  link_pdf?: string
  link_editavel?: string
  plano_negocio?: string
  link_origem_lead?: string
  status_entrega?: boolean
  created_at?: string
  updated_at?: string
  capa?: {
    imagem_capa?: string
    imagem_capa_png?: string
    titulo_livro?: string
  }
  services?: {
    livro?: boolean
    capa?: boolean
    isbn?: boolean
    amazon?: boolean
    traducao?: boolean
    percentComplete?: number
    nextStep?: string
    etapasCompletas?: number
    totalEtapas?: number
  }
}

interface FiltrosLivros {
  assunto?: string
  idioma?: string
  status_entrega?: string
  data_inicio?: string
  data_fim?: string
}

interface RespostaLivros {
  data: Livro[]
  count: number
  error?: string
}

interface RespostaCliente {
  data: Cliente | null
  error?: string
}

interface LivroCompleto extends Livro {
  cliente: Cliente
  pedido_info: Pedido
}

// Configuração do Supabase via Variáveis de Ambiente
const API_ENDPOINTS = {
  REST_BASE: import.meta.env.VITE_SUPABASE_REST_BASE || 'https://supavps2.responda-ai.com/rest/v1',
  AUTH_BASE: import.meta.env.VITE_SUPABASE_AUTH_BASE || 'https://supavps2.responda-ai.com/auth/v1'
} as const

// No frontend usamos APENAS a chave anônima (segura para expor)
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

const getHeaders = () => ({
  'apikey': SUPABASE_ANON_KEY,
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json'
})

/**
 * 🔍 Classe principal para interação com o Supabase
 * Implementa todas as operações necessárias para o dashboard
 */
export class SupabaseService {
  
  static async buscarClientePorEmail(email: string): Promise<RespostaCliente> {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.REST_BASE}/clientes?email=eq.${encodeURIComponent(email)}&limit=1`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`)
      }

      const data = await response.json()
      
      return {
        data: data.length > 0 ? data[0] : null,
        error: data.length === 0 ? 'Cliente não encontrado' : undefined
      }
      
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      }
    }
  }

  static async buscarLivrosDoCliente(
    clienteId: string | number,
    filtros: FiltrosLivros = {},
    limite = 20,
    offset = 0
  ): Promise<RespostaLivros> {
    try {
      const normalizedClienteId = String(clienteId).trim()
      if (!normalizedClienteId) {
        return { data: [], count: 0, error: 'Cliente inválido' }
      }

      const pedidosRes = await fetch(
        `${API_ENDPOINTS.REST_BASE}/pedidos?select=id_pedido&id_cliente=eq.${encodeURIComponent(normalizedClienteId)}`,
        { headers: getHeaders() }
      )
      if (!pedidosRes.ok) {
        throw new Error(`HTTP ${pedidosRes.status}: ${await pedidosRes.text()}`)
      }
      const pedidosData = await pedidosRes.json() as Array<{ id_pedido: string }>
      const idsPedido = pedidosData.map(p => p.id_pedido).filter(Boolean)

      if (idsPedido.length === 0) {
        return { data: [], count: 0, error: undefined }
      }

      const inList = `(${idsPedido.map(id => `"${id}"`).join(',')})`
      let query = `${API_ENDPOINTS.REST_BASE}/livros?select=*` +
                  `&id_pedido=in.${encodeURIComponent(inList)}`

      if (filtros.assunto) {
        query += `&assunto=eq.${encodeURIComponent(filtros.assunto)}`
      }
      if (filtros.idioma) {
        query += `&idioma=eq.${encodeURIComponent(filtros.idioma)}`
      }
      if (typeof (filtros as any).status_entrega !== 'undefined') {
        const val = (filtros as any).status_entrega ? 'true' : 'false'
        query += `&status_entrega=eq.${val}`
      }

      query += `&limit=${limite}&offset=${offset}&order=id_livro.desc`

      const response = await fetch(query, {
        method: 'GET',
        headers: {
          ...getHeaders(),
          'Prefer': 'count=exact'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`)
      }

      const data = await response.json()
      const countHeader = response.headers.get('content-range')
      const count = countHeader ? parseInt(countHeader.split('/')[1]) : data.length
      const normalized = data.map((l: any) => ({ id: l.id_livro ?? l.id, ...l }))

      const comCapas = await this.adicionarCapasAosLivros(normalized)
      const livrosEnriquecidos = await this.adicionarServicosAosLivros(comCapas)

      return { data: livrosEnriquecidos, count, error: undefined }

    } catch (error) {
      return {
        data: [],
        count: 0,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      }
    }
  }

  static async buscarDetalhesLivro(livroId: number): Promise<LivroCompleto | null> {
    try {
      const query = `${API_ENDPOINTS.REST_BASE}/livros?select=*,pedidos!inner(*,clientes!inner(*))&id=eq.${livroId}&limit=1`

      const response = await fetch(query, {
        method: 'GET',
        headers: getHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`)
      }

      const data = await response.json()
      
      if (data.length === 0) {
        return null
      }

      const livro = data[0]
      
      return {
        ...livro,
        cliente: livro.clientes,
        pedido_info: livro.pedidos
      }
      
    } catch (error) {
      return null
    }
  }

  static async buscarLivrosDisponiveis(
    clienteId: string | number,
    _limite = 20,
    _offset = 0
  ): Promise<RespostaLivros> {
    try {
      const normalizedClienteId = String(clienteId).trim()
      if (!normalizedClienteId) {
        return { data: [], count: 0, error: 'Cliente inválido' }
      }

      const pedidosRes = await fetch(
        `${API_ENDPOINTS.REST_BASE}/pedidos?select=id_pedido&id_cliente=eq.${encodeURIComponent(normalizedClienteId)}`,
        { headers: getHeaders() }
      )
      if (!pedidosRes.ok) {
        throw new Error(`HTTP ${pedidosRes.status}: ${await pedidosRes.text()}`)
      }
      const pedidosData = await pedidosRes.json() as Array<{ id_pedido: string }>
      const idsPedido = pedidosData.map(p => p.id_pedido).filter(Boolean)
      if (idsPedido.length === 0) {
        return { data: [], count: 0, error: undefined }
      }

      const inList = `(${idsPedido.map(id => `"${id}"`).join(',')})`
      const query = `${API_ENDPOINTS.REST_BASE}/livros?select=*` +
                    `&id_pedido=in.${encodeURIComponent(inList)}` +
                    `&status_entrega=eq.true` +
                    `&order=id_livro.desc`

      const response = await fetch(query, {
        method: 'GET',
        headers: {
          ...getHeaders(),
          'Prefer': 'count=exact'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`)
      }

      const data = await response.json()
      const countHeader = response.headers.get('content-range')
      const count = countHeader ? parseInt(countHeader.split('/')[1]) : data.length
      const normalized = data.map((l: any) => ({ id: l.id_livro ?? l.id, ...l }))

      const comCapas = await this.adicionarCapasAosLivros(normalized)
      const livrosComServicos = await this.adicionarServicosAosLivros(comCapas)

      return { data: livrosComServicos, count, error: undefined }

    } catch (error) {
      return { data: [], count: 0, error: error instanceof Error ? error.message : 'Erro desconhecido' }
    }
  }

  static async buscarCapasDosPedidos(idsPedido: string[]): Promise<Record<string, any>> {
    try {
      if (idsPedido.length === 0) return {}

      const inList = `(${idsPedido.map(id => `"${id}"`).join(',')})`
      const query = `${API_ENDPOINTS.REST_BASE}/capas_livros?select=id_pedido,imagem_capa,imagem_capa_png,titulo_livro` +
                    `&id_pedido=in.${encodeURIComponent(inList)}`

      const response = await fetch(query, {
        method: 'GET',
        headers: getHeaders()
      })

      if (!response.ok) {
        return {}
      }

      const capas = await response.json()

      const capasPorPedido: Record<string, any> = {}
      capas.forEach((capa: any) => {
        capasPorPedido[capa.id_pedido] = {
          imagem_capa: capa.imagem_capa,
          imagem_capa_png: capa.imagem_capa_png,
          titulo_livro: capa.titulo_livro
        }
      })

      return capasPorPedido

    } catch (error) {
      return {}
    }
  }

  static async adicionarCapasAosLivros(livros: any[]): Promise<any[]> {
    try {
      const idsPedido = [...new Set(livros.map(l => l.id_pedido).filter(Boolean))]
      const capasPorPedido = await this.buscarCapasDosPedidos(idsPedido)

      return livros.map(livro => ({
        ...livro,
        capa: capasPorPedido[livro.id_pedido] || null
      }))

    } catch (error) {
      return livros
    }
  }

  static async buscarItensDosPedidos(idsPedido: string[]): Promise<Record<string, any[]>> {
    try {
      if (idsPedido.length === 0) return {}

      const inList = `(${idsPedido.map(id => `"${id}"`).join(',')})`
      const query = `${API_ENDPOINTS.REST_BASE}/itens_do_pedido?select=*` +
                    `&id_pedido=in.${encodeURIComponent(inList)}`

      const response = await fetch(query, {
        method: 'GET',
        headers: getHeaders()
      })

      if (!response.ok) {
        return {}
      }

      const itens = await response.json()

      const itensPorPedido: Record<string, any[]> = {}
      itens.forEach((item: any) => {
        if (!itensPorPedido[item.id_pedido]) {
          itensPorPedido[item.id_pedido] = []
        }
        itensPorPedido[item.id_pedido].push(item)
      })

      return itensPorPedido

    } catch (error) {
      return {}
    }
  }

  static async adicionarServicosAosLivros(livros: any[]): Promise<any[]> {
    try {
      const idsPedido = [...new Set(livros.map(l => l.id_pedido).filter(Boolean))]
      const itensPorPedido = await this.buscarItensDosPedidos(idsPedido)

      return livros.map(livro => {
        const itensDoLivro = itensPorPedido[livro.id_pedido] || []
        const services = this.mapearServicosDoLivro(itensDoLivro)
        services.livro = services.livro || !!livro.status_entrega

        return {
          ...livro,
          services
        }
      })

    } catch (error) {
      return livros
    }
  }

  private static mapearServicosDoLivro(itens: any[]): any {
    const SERVICO_IDS = {
      LIVRO: 85665,
      CAPA: 85666,
      ISBN: 87453,
      AMAZON: 104560,
      TRADUCAO: 94680
    }

    const itensPagos = itens.filter(item => item.status === 'Pago')
    const servicosPorTipo: Record<number, any> = {}

    itensPagos.forEach(item => {
      const idProduto = parseInt(item.id_produto)
      if (!servicosPorTipo[idProduto] || item.id_item_pedido > servicosPorTipo[idProduto].id_item_pedido) {
        servicosPorTipo[idProduto] = item
      }
    })

    const services = {
      livro: !!servicosPorTipo[SERVICO_IDS.LIVRO],
      capa: !!servicosPorTipo[SERVICO_IDS.CAPA],
      isbn: !!servicosPorTipo[SERVICO_IDS.ISBN],
      amazon: !!servicosPorTipo[SERVICO_IDS.AMAZON],
      traducao: !!servicosPorTipo[SERVICO_IDS.TRADUCAO]
    }

    const etapasCompletas = Object.values(services).filter(Boolean).length
    const percentComplete = Math.round((etapasCompletas / 5) * 100)

    let nextStep = 'done'
    if (!services.livro) nextStep = 'livro'
    else if (!services.capa) nextStep = 'capa'
    else if (!services.isbn) nextStep = 'isbn'
    else if (!services.amazon) nextStep = 'amazon'
    else if (!services.traducao) nextStep = 'traducao'

    return {
      ...services,
      percentComplete,
      nextStep,
      etapasCompletas,
      totalEtapas: 5
    }
  }

  static async validarLinkDownload(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return response.ok
    } catch {
      return false
    }
  }
}